// Libraries
import React, {createContext} from 'react';

// Supermove
import {useCallback, useNavigation, useNavigationDOM} from '@supermove/hooks';

import {NavigationType, ParamsType} from '../NavigationTypes';

export interface NavigationContextType {
  navigator: NavigationType;
  params: ParamsType;
}

// This will look like {[Routes.GenericRouteName]: 'GenericRouteName'}
export const Routes: Record<string, string> = {} as const;

export type RouteNameType = keyof typeof Routes;

// TODO(jay): After we upgrade to new React Navigation, we can see about strict typing with params
// In each app, this would look like:
// {
//   GenericRouteName: '[whatever the path should be in each app]',
// }
export type RoutingTableType = Partial<Record<RouteNameType, string>>;

const NavigationContext = createContext<NavigationContextType>({
  navigator: {} as NavigationType,
  params: {},
});

// This provides a navigation context for apps using react-router-dom
// This wraps the react-router-dom `push` function with `navigate`,
// And supports passing in params as an object and adding it to the route string
export const NavigationDomContextProvider = ({
  children,
  routingTable,
}: {
  children: React.ReactNode;
  routingTable: RoutingTableType;
}) => {
  const {navigator, params} = useNavigationDOM();
  const originalNavigate = navigator.push;
  const originalReplace = navigator.replace;

  // The react router navigation doesn't take params as a separate block, so dynamically add it to the route string
  const getRouteWithParams = useCallback(
    (routeName: string, params: ParamsType = {}) => {
      const mappedRouteName = routingTable[routeName as RouteNameType];
      if (!mappedRouteName) {
        throw new Error(`Route name '${routeName}' not found in routing table`);
      }
      // This looks in the RouteTable, replaces any matching path params with the values from the params object,
      // and if a path param is not found adds it as a query param
      const routeWithParams = Object.entries(params).reduce((acc, [key, value]) => {
        // Replace path params if present
        if (acc.includes(`:${key}`)) {
          return acc.replace(new RegExp(`:${key}\\b`, 'g'), value);
        }

        // Otherwise append as query string
        const separator = acc.includes('?') ? '&' : '?';
        return `${acc}${separator}${key}=${value}`;
      }, mappedRouteName);
      return routeWithParams;
    },
    [routingTable],
  );

  navigator.navigate = useCallback(
    (routeName: string, params?: ParamsType) => {
      const routeWithParams = getRouteWithParams(routeName, params);
      originalNavigate(routeWithParams);
    },
    [getRouteWithParams],
  );

  navigator.replace = useCallback(
    (routeName: string, params?: ParamsType) => {
      const routeWithParams = getRouteWithParams(routeName, params);
      originalReplace(routeWithParams);
    },
    [getRouteWithParams],
  );

  return (
    <NavigationContext.Provider value={{navigator, params}}>{children}</NavigationContext.Provider>
  );
};

// This provides a navigation context for apps using react-navigation
export const NavigationContextProvider = ({
  children,
  routingTable,
}: {
  children: React.ReactNode;
  routingTable: RoutingTableType;
}) => {
  const {navigator, params} = useNavigation();
  const originalNavigate = navigator.navigate;
  const originalReplace = navigator.replace;

  const getRouteWithParams = useCallback(
    (routeName: string) => {
      const mappedRouteName = routingTable[routeName as RouteNameType];
      if (!mappedRouteName) {
        throw new Error(`Route name '${routeName}' not found in routing table`);
      }
      return mappedRouteName;
    },
    [routingTable],
  );

  navigator.navigate = useCallback(
    (routeName: string, params?: ParamsType) => {
      const mappedRouteName = getRouteWithParams(routeName);
      originalNavigate(mappedRouteName, params);
    },
    [getRouteWithParams],
  );

  navigator.replace = useCallback(
    (routeName: string, params?: ParamsType) => {
      const mappedRouteName = getRouteWithParams(routeName);
      originalReplace(mappedRouteName, params);
    },
    [getRouteWithParams],
  );

  return (
    <NavigationContext.Provider value={{navigator, params}}>{children}</NavigationContext.Provider>
  );
};

export default NavigationContext;
